import React, { useState, useEffect } from 'react';
import { X, Package, Truck, ArrowRightLeft, Send, Loader2, AlertCircle, AlertTriangle, CreditCard } from 'lucide-react';
import { api, openPricingPlans } from '../lib/api';
import { kindForCentreType } from '../lib/deliveryPoints';
import ServiceGuideModal from './ServiceGuideModal';
import ServiceGuideButton from './ServiceGuideButton';
import DeliveryPointField from './DeliveryPointField';

/**
 * Creates an HP label in either direction.
 *
 * OUTBOUND -- merchant ships to the customer. The merchant is the HP sender and
 *             chooses how HP delivers (address, post office, parcel locker) and
 *             whether to collect cash on delivery.
 * RETURN   -- customer ships back. The customer is the HP sender and chooses how
 *             they hand the parcel over; delivery is always the merchant's
 *             address, so there is nothing to pick.
 *
 * The two forms share every field that describes the parcel itself, so they
 * share one component rather than two that drift apart.
 */

const SERVICE_OPTIONS = {
  // A return is an ordinary parcel travelling the other way, so the Paket24
  // tiers are offered alongside EasyReturn.
  RETURN: [
    [40, 'EasyReturn D+3 (recommended)'],
    [39, 'EasyReturn D+3 (paired with outbound)'],
    [26, 'Paket24 D+1 Express'],
    [29, 'Paket24 D+2 Standard'],
    [32, 'Paket24 D+3 Economy'],
  ],
  // EasyReturn codes are return-only: HP requires them to be paid by the
  // recipient, which is the wrong end of an outbound shipment.
  OUTBOUND: [
    [29, 'Paket24 D+2 Standard (recommended)'],
    [26, 'Paket24 D+1 Express'],
    [32, 'Paket24 D+3 Economy'],
  ],
};

// Mirrors HR_MOBILE_RE in server/hpService.js. HP sends the parcel locker PIN
// by SMS, so it rejects anything that is not a Croatian mobile number.
const HR_MOBILE_RE = /^(?:\+385|00385|385)?0?9[125789]\d{6,7}$/;
const isCroatianMobile = (phone) => HR_MOBILE_RE.test(String(phone || '').replace(/[\s\-().]/g, ''));

const COPY = {
  OUTBOUND: {
    title: 'Create HP shipping label',
    icon: Send,
    iconBg: '#e6f4ea',
    iconColor: '#0d522c',
    submit: 'Generate shipping label',
    submitting: 'Creating shipping label at HP...',
    handoverLabel: 'How you hand the parcel to HP',
    handoverOptions: [
      [1, 'Courier collects from your address'],
      [2, 'You drop off at a post office'],
      [3, 'You drop off at a parcel locker'],
    ],
    senderTitle: 'Sender (your warehouse)',
    recipientTitle: 'Recipient (customer)',
  },
  RETURN: {
    title: 'Create HP return label',
    icon: ArrowRightLeft,
    iconBg: '#ffeeee',
    iconColor: '#e20613',
    submit: 'Generate return label',
    submitting: 'Creating return label at HP...',
    handoverLabel: 'Pickup method (from customer)',
    handoverOptions: [
      [1, 'Address pickup (courier collects)'],
      [2, 'Post office drop-off (poštanski ured)'],
      [3, 'Parcel locker drop-off (paketomat)'],
    ],
    senderTitle: 'Sender (customer)',
    recipientTitle: 'Recipient (your warehouse)',
  },
};

const banner = (background, border, color) => ({
  background,
  border: `1px solid ${border}`,
  color,
  padding: '0.75rem 1rem',
  borderRadius: '6px',
  fontSize: '0.85rem',
  marginBottom: '1.25rem',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
});

/**
 * The `if (!isOpen) return null` guard sits *below* the hooks. It used to be the
 * first statement in the component, which meant the hook call order changed
 * between renders -- a rules-of-hooks violation React only sometimes forgives.
 */
export default function CreateLabelModal({ isOpen, onClose, order, direction = 'RETURN', onLabelCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Set instead of `error` when the server refuses on quota, so the merchant
  // gets an upgrade button rather than a red dead end.
  const [quotaBlock, setQuotaBlock] = useState(null);

  const [serviceCode, setServiceCode] = useState(40);
  const [pickupType, setPickupType] = useState(2);
  const [pickupCenter, setPickupCenter] = useState('');
  const [deliveryType, setDeliveryType] = useState(1);
  const [deliveryCenter, setDeliveryCenter] = useState('');
  const [codAmount, setCodAmount] = useState('');
  const [parcelSize, setParcelSize] = useState('S');
  const [weight, setWeight] = useState(1.0);
  const [volumeX, setVolumeX] = useState(20);
  const [volumeY, setVolumeY] = useState(15);
  const [volumeZ, setVolumeZ] = useState(10);
  const [merchantAddress, setMerchantAddress] = useState(null);

  // The HP service reference, stacked over this modal.
  const [guideOpen, setGuideOpen] = useState(false);

  const outbound = direction === 'OUTBOUND';
  const copy = COPY[direction];

  // Pre-fill from the merchant's saved defaults each time the modal opens, and
  // show the real warehouse address instead of a placeholder.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setQuotaBlock(null);
    setCodAmount('');
    // Centre codes are per-order, so they must not survive into the next order
    // the merchant opens this modal for.
    setPickupCenter('');
    setDeliveryCenter('');

    api
      .getSettings()
      .then(({ settings }) => {
        setServiceCode(outbound ? settings.defaultShippingServiceCode : settings.defaultServiceCode);
        // An outbound parcel is handed over by the merchant, who normally has a
        // courier booked; the saved pickup default describes the customer's
        // habits and does not transfer.
        setPickupType(outbound ? 1 : settings.defaultPickupType);
        setDeliveryType(settings.defaultDeliveryType);
        setParcelSize(settings.defaultParcelSize);
        setWeight(settings.defaultWeight);
        setMerchantAddress(settings.merchantAddress);
      })
      .catch((err) => setError(`Could not load your saved defaults: ${err.message}`));
  }, [isOpen, outbound]);

  if (!isOpen || !order) return null;

  const handoverAtCentre = Number(pickupType) === 2 || Number(pickupType) === 3;
  const deliverToCentre = outbound && (Number(deliveryType) === 2 || Number(deliveryType) === 3);
  const toLocker = outbound && Number(deliveryType) === 3;
  const cod = Number(codAmount) > 0;

  // Warned about rather than blocked: HP's own documentation forbids COD on
  // parcel locker delivery, but the TEST environment accepts it. Let HP be the
  // authority instead of refusing something it may well allow.
  const codAtLockerWarning = toLocker && cod;
  const phoneWarning = toLocker && order.customer?.phone && !isCroatianMobile(order.customer.phone);

  // Seeds for the delivery point search. The parcel is handed over near
  // whoever is sending it and collected near whoever is receiving it, so the
  // picker opens on that town instead of the whole country.
  const customerTown = order.shippingAddress?.city || order.shippingAddress?.zip || '';
  const merchantTown = merchantAddress?.city || merchantAddress?.zip || '';

  // A post office number is not a parcel locker code and vice versa, so
  // changing where the parcel changes hands clears the code that was there.
  // Otherwise the field silently keeps a code HP will reject.
  const changePickupType = (value) => {
    setPickupType(value);
    setPickupCenter('');
  };

  const changeDeliveryType = (value) => {
    setDeliveryType(value);
    setDeliveryCenter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setQuotaBlock(null);

    const payload = {
      serviceCode: Number(serviceCode),
      pickupType: Number(pickupType),
      pickupCenter: handoverAtCentre ? pickupCenter : null,
      parcelSize,
      weight: Number(weight),
      volumeX: Number(volumeX),
      volumeY: Number(volumeY),
      volumeZ: Number(volumeZ),
      ...(outbound
        ? {
            deliveryType: Number(deliveryType),
            deliveryCenter: deliverToCentre ? deliveryCenter : null,
            codAmount: cod ? Number(codAmount) : 0,
          }
        : {}),
    };

    try {
      const data = outbound
        ? await api.createShippingLabel(order.id, payload)
        : await api.createReturnLabel(order.id, payload);
      onLabelCreated(data.label, order.name, data.usage, direction);
      onClose();
    } catch (err) {
      if (err.isQuotaExceeded) {
        setQuotaBlock({ message: err.message, billing: err.billing });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const TitleIcon = copy.icon;

  const customerBlock = (
    <>
      <div>{order.customer?.name}</div>
      {order.shippingAddress ? (
        <div>
          {order.shippingAddress.address1}, {order.shippingAddress.zip} {order.shippingAddress.city}
        </div>
      ) : (
        <div style={{ color: '#c5221f' }}>No shipping address on this order</div>
      )}
    </>
  );

  const merchantBlock = merchantAddress?.companyName ? (
    <>
      <div>{merchantAddress.companyName}</div>
      <div>
        {merchantAddress.street} {merchantAddress.hnum}, {merchantAddress.zip} {merchantAddress.city}
      </div>
    </>
  ) : (
    <div style={{ color: '#b98900' }}>Set your address in Plugin Settings</div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ background: copy.iconBg, color: copy.iconColor, padding: '0.4rem', borderRadius: '6px', display: 'flex' }}>
              <TitleIcon size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>{copy.title}</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)' }}>
                Order {order.name} • {order.customer?.name}
              </p>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={loading} style={{ padding: '0.4rem' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {quotaBlock && (
              <div style={banner('#fff8e1', '#f0d48a', '#6b4e00')}>
                <CreditCard size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <p style={{ marginBottom: '0.6rem' }}>{quotaBlock.message}</p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => openPricingPlans(quotaBlock.billing?.upgradeUrl)}
                  >
                    <CreditCard size={14} /> See plans
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div style={banner('#fce8e6', '#f3b8b4', '#c5221f')}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>{error}</span>
              </div>
            )}

            {phoneWarning && (
              <div style={banner('#fce8e6', '#f3b8b4', '#c5221f')}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>
                  <strong>{order.customer.phone}</strong> is not a Croatian mobile number, and HP sends
                  the parcel locker PIN by SMS. Hrvatska Pošta will reject this label — update the phone
                  number on the order, or deliver to the address instead.
                </span>
              </div>
            )}

            {codAtLockerWarning && (
              <div style={banner('#fff8e1', '#f0d48a', '#6b4e00')}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>
                  HP's documentation says cash on delivery is not allowed on parcel locker delivery,
                  though their API currently accepts it. If this label is rejected, deliver to the
                  address instead.
                </span>
              </div>
            )}

            <div
              style={{
                background: 'var(--color-bg-subtle)',
                padding: '0.85rem 1rem',
                borderRadius: '8px',
                border: '1px solid var(--color-border-subtle)',
                marginBottom: '1.25rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: 'var(--color-secondary)',
                  marginBottom: '0.4rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                Order items
              </div>
              <ul style={{ listStyle: 'none', fontSize: '0.88rem' }}>
                {order.lineItems.map((item) => (
                  <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span>
                      {item.quantity}x <strong>{item.title}</strong>
                    </span>
                    <span style={{ color: 'var(--color-secondary)' }}>
                      {new Intl.NumberFormat('hr-HR', {
                        style: 'currency',
                        currency: order.currency || 'EUR',
                      }).format(item.price)}
                    </span>
                  </li>
                ))}
              </ul>
              {!outbound && order.returnReason && (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', marginTop: '0.5rem' }}>
                  Return reason: {order.returnReason}
                </p>
              )}
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  HP shipping service
                  <ServiceGuideButton onClick={() => setGuideOpen(true)} />
                </label>
                <select className="form-control" value={serviceCode} onChange={(e) => setServiceCode(e.target.value)}>
                  {SERVICE_OPTIONS[direction].map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{copy.handoverLabel}</label>
                <select className="form-control" value={pickupType} onChange={(e) => changePickupType(e.target.value)}>
                  {copy.handoverOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {handoverAtCentre && (
              <DeliveryPointField
                kind={kindForCentreType(pickupType)}
                label={
                  Number(pickupType) === 3 ? 'Paketomat code (hand-over)' : 'Post office code (hand-over)'
                }
                value={pickupCenter}
                onChange={setPickupCenter}
                initialSearch={outbound ? merchantTown : customerTown}
                help="Where the parcel is handed over to HP. Browse to search the live HP network, or type the code if you know it."
                required
              />
            )}

            {outbound && (
              <>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">How HP delivers to the customer</label>
                    <select className="form-control" value={deliveryType} onChange={(e) => changeDeliveryType(e.target.value)}>
                      <option value={1}>To their address</option>
                      <option value={2}>To a post office (poštanski ured)</option>
                      <option value={3}>To a parcel locker (paketomat)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cash on delivery (EUR)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="13300"
                      className="form-control"
                      value={codAmount}
                      onChange={(e) => setCodAmount(e.target.value)}
                      placeholder="Leave empty for no COD"
                    />
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.25rem' }}>
                      Adds HP additional service 9 (pouzeće). Maximum 13,300.00 EUR.
                    </p>
                  </div>
                </div>

                {deliverToCentre && (
                  <DeliveryPointField
                    kind={kindForCentreType(deliveryType)}
                    label={
                      Number(deliveryType) === 3
                        ? 'Paketomat code (delivery)'
                        : 'Post office code (delivery)'
                    }
                    value={deliveryCenter}
                    onChange={setDeliveryCenter}
                    initialSearch={customerTown}
                    help="Where the customer collects the parcel. Browse to search near their town, or type the code if you know it."
                    required
                  />
                )}
              </>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Parcel compartment size</label>
                <select className="form-control" value={parcelSize} onChange={(e) => setParcelSize(e.target.value)}>
                  <option value="XS">XS (9 x 16 x 64 cm)</option>
                  <option value="S">S (9 x 38 x 64 cm)</option>
                  <option value="M">M (19 x 38 x 64 cm)</option>
                  <option value="L">L (39 x 38 x 64 cm)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="30"
                  className="form-control"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  required
                />
              </div>
            </div>

            <div
              style={{
                background: '#f8fafc',
                padding: '0.85rem 1rem',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginTop: '0.5rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: '#334155',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                <Truck size={15} color="#008060" />
                <span>Parcel route</span>
              </div>
              <div className="route-grid" style={{ fontSize: '0.82rem', color: '#475569' }}>
                <div>
                  <strong style={{ color: '#0f172a' }}>{copy.senderTitle}</strong>
                  {outbound ? merchantBlock : customerBlock}
                </div>
                <div>
                  <strong style={{ color: '#0f172a' }}>{copy.recipientTitle}</strong>
                  {outbound ? customerBlock : merchantBlock}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-hp" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} className="spin" /> {copy.submitting}
                </>
              ) : (
                <>
                  <Package size={16} /> {copy.submit}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <ServiceGuideModal isOpen={guideOpen} highlight={direction} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
